import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AdjustLicencesDialogComponent } from '../../Dialogs/adjust-licences-dialog/adjust-licences-dialog.component';
import { ResumeLicencesDialogComponent } from '../../Dialogs/resume-licences-dialog/resume-licences-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { OrganizationLicenceFacade } from '../../../../../Facades/Organizations/organization-licence.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  LICENCE_CANCEL_CONFIRM_TITLE,
  LICENCE_COMPONENT_OPTIONS,
  LICENCE_HOLDER_OPTIONS,
  LICENCE_RESUME_CONFIRM_TITLE,
  LICENCE_STATE_OPTIONS,
} from '../../../../../Consts/licences.const';
import {
  LICENCE_HOLDER_LABEL,
  LICENCE_STATE_LABEL,
} from '../../../../../Enums/licence-state.enum';
import { PRICE_COMPONENT_LABEL } from '../../../../../Enums/price-component-type.enum';
import { Licence } from '../../../../../Models/backoffice/licence.model';
import {
  AdjustLicencesDialogResult,
  ResumeLicencesDialogResult,
} from '../../../Interfaces/licence-dialog.interface';

@Component({
  selector: 'av-organization-licences',
  standalone: true,
  imports: [
    RouterLink,
    DatePipe,
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './organization-licences.component.html',
  styleUrl: './organization-licences.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class OrganizationLicencesComponent {
  private readonly _dialog = inject(MatDialog);
  private readonly _translate = inject(TranslateService);

  readonly facade = inject(OrganizationLicenceFacade);

  readonly organizationId = input<string>('');

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly componentOptions = LICENCE_COMPONENT_OPTIONS;
  readonly holderOptions = LICENCE_HOLDER_OPTIONS;
  readonly stateOptions = LICENCE_STATE_OPTIONS;
  readonly componentLabelKeys = PRICE_COMPONENT_LABEL;
  readonly holderLabelKeys = LICENCE_HOLDER_LABEL;
  readonly stateLabelKeys = LICENCE_STATE_LABEL;

  readonly isEmpty = computed<boolean>(
    () => this.facade.state() === 'ready' && this.facade.licences().length === 0,
  );

  readonly hasPendingCancellation = computed<boolean>(() =>
    this.facade.components().some((component) => component.hasPendingCancellation),
  );

  filterComponent(value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), component_type: value });
  }

  filterHolder(value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), holder: value });
  }

  filterState(value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), state: value });
  }

  clearFilters(): void {
    this.facade.clearFilters();
  }

  goToPage(page: number): void {
    this.facade.goToPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  openAdjust(): void {
    const reference = this._dialog.open(AdjustLicencesDialogComponent, {
      data: { components: this.facade.components() },
    });

    reference
      .afterClosed()
      .subscribe((result?: AdjustLicencesDialogResult) => this._applyAdjust(result));
  }

  openResume(): void {
    const reference = this._dialog.open(ResumeLicencesDialogComponent, {
      data: { components: this.facade.components() },
    });

    reference
      .afterClosed()
      .subscribe((result?: ResumeLicencesDialogResult) => this._applyResume(result));
  }

  cancelOne(licence: Licence): void {
    this.facade.cancelOne(licence.id, this._translate.instant(LICENCE_CANCEL_CONFIRM_TITLE));
  }

  resumeOne(licence: Licence): void {
    this.facade.resumeOne(licence.id, this._translate.instant(LICENCE_RESUME_CONFIRM_TITLE));
  }

  private _applyAdjust(result?: AdjustLicencesDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.adjust({
      component_type: result.componentType,
      delta: result.delta,
      reason: result.reason,
    });
  }

  private _applyResume(result?: ResumeLicencesDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.resumeQuantity({
      component_type: result.componentType,
      quantity: result.quantity,
      reason: result.reason,
    });
  }
}
